import { useQuery } from "@tanstack/react-query";
import type { CardTopUps } from "@/lib/wallet";

// Unauthenticated platform status (GET /api/public/config): maintenance,
// sign-ups and card top-up options. Fetched without the API client so it
// never sends credentials and works on the sign-in/sign-up pages.

export interface PublicConfig {
  maintenance: boolean;
  allowRegistration: boolean;
  cardTopUps: CardTopUps;
}

export const PUBLIC_CONFIG_KEY = ["public-config"] as const;

const num = (v: unknown, fallback: number) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : fallback;
};

export function parseCardTopUps(raw: unknown): CardTopUps {
  const c = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const minimum = num(c.minimum, 5);
  const maximum = num(c.maximum, 1000);
  const presets = (Array.isArray(c.presets) ? c.presets : [])
    .map((p) => num(p, NaN))
    .filter((p) => Number.isFinite(p) && p >= minimum && p <= maximum);
  return {
    enabled: !!c.enabled,
    currency: typeof c.currency === "string" ? c.currency : "USD",
    minimum,
    maximum,
    presets,
  };
}

export function usePublicConfig({ refetchInterval }: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: PUBLIC_CONFIG_KEY,
    staleTime: 60 * 1000,
    refetchInterval,
    queryFn: async (): Promise<PublicConfig> => {
      const res = await fetch("/api/public/config", { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const d = body?.data ?? {};
      return {
        maintenance: d.maintenance === true,
        allowRegistration: d.allowRegistration !== false,
        cardTopUps: parseCardTopUps(d.cardTopUps),
      };
    },
  });
}
